import { Bike, Bus, Car, Container, Truck, type LucideIcon } from 'lucide-react';
import type {
  CheckRegime,
  CheckTemplateQuestion,
  QuestionSeverity,
  TemplateVersionStatus,
  UnitType,
} from '@my/check-api';

// ────────────────────────────────────────────────────────────────────────────
// Presentation layer for check templates.
//
// The domain types now come from @my/check-api, which mirrors the BE DTOs.
// This file holds only what the BE has no opinion about: which icon and blurb
// each unit type gets, how a severity or status renders, and the ordering
// helpers the screens need.
//
// The FE no longer carries its own copy of the question catalogue — the BE
// serves it from checkQuestionCatalogue.json and a second copy here would
// drift, exactly as AppName did across the seven APIs.
// ────────────────────────────────────────────────────────────────────────────

export interface UnitTypeDef {
  key: UnitType;
  label: string;
  icon: LucideIcon;
  description: string;
}

/**
 * Unit types per regime. Split because the two regimes cover disjoint fleets:
 * WAC is the statutory walk-around, Pre-Use is a company-policy check on work
 * vehicles under health-and-safety duties.
 *
 * `Motorcycle` and `Cycle` replace the earlier single `Bike` — they differ
 * legally (a motorcycle is a mechanically propelled vehicle) and in content
 * (chain tension is meaningless on a shaft-drive motorcycle). The BE enum
 * carries both.
 */
export const WAC_UNIT_TYPES: UnitTypeDef[] = [
  { key: 'Hgv', label: 'HGV / Artic', icon: Truck, description: 'Articulated HGV — formal WAC required' },
  { key: 'Rigid', label: 'Rigid', icon: Truck, description: 'Rigid body truck' },
  { key: 'Lgv', label: 'LGV / Van', icon: Truck, description: 'Light goods vehicle, commercial van' },
  { key: 'Trailer', label: 'Trailer', icon: Container, description: 'Semi-trailer / curtainsider / flatbed' },
  { key: 'Bus', label: 'Bus / Coach', icon: Bus, description: 'PSV — full-size bus or coach' },
  { key: 'Minibus', label: 'Minibus', icon: Bus, description: 'PSV — 9 to 16 passenger seats' },
];

export const PREUSE_UNIT_TYPES: UnitTypeDef[] = [
  { key: 'Car', label: 'Car', icon: Car, description: 'Company car / pool vehicle' },
  { key: 'Motorcycle', label: 'Motorcycle', icon: Bike, description: 'Motorcycle or moped' },
  { key: 'Cycle', label: 'Cycle', icon: Bike, description: 'Cargo bike or pedal cycle' },
];

export function unitTypesFor(regime: CheckRegime): UnitTypeDef[] {
  return regime === 'Wac' ? WAC_UNIT_TYPES : PREUSE_UNIT_TYPES;
}

export function unitLabel(unit: UnitType): string {
  return [...WAC_UNIT_TYPES, ...PREUSE_UNIT_TYPES].find((u) => u.key === unit)?.label ?? unit;
}

export const SEVERITY_OPTIONS: QuestionSeverity[] = ['Low', 'Medium', 'High'];

/** Severity select tint. */
export const SEVERITY_SELECT_CLASS: Record<QuestionSeverity, string> = {
  Low: 'border-emerald-300 text-emerald-700',
  Medium: 'border-amber-300 text-amber-700',
  High: 'border-red-300 text-red-700',
};

export const STATUS_BADGE: Record<TemplateVersionStatus, string> = {
  Draft: 'border-slate-300 bg-white text-slate-600',
  Published: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  Archived: 'border-slate-200 bg-slate-100 text-slate-500',
};

/**
 * A question being edited. Extends the API shape with a client-only `key` so
 * React lists and reordering stay stable for rows that don't have a server id
 * yet (a custom question added but not saved).
 */
export interface EditableQuestion extends Omit<CheckTemplateQuestion, 'id'> {
  /** Server id, or null for a row added in this editing session. */
  id: string | null;
  /** Stable client-side identity for keys and reordering. */
  key: string;
}

export function toEditable(q: CheckTemplateQuestion): EditableQuestion {
  return { ...q, key: q.id };
}

/**
 * Section keys in the order the questions themselves imply.
 *
 * Ordering comes from `sortOrder` rather than a catalogue lookup: the BE
 * assigns sortOrder across the whole version in section order, and each
 * question carries its own `sectionLabel`, so the screens need nothing else.
 * That is what lets the FE drop its catalogue copy.
 */
export function sectionsInOrder(
  questions: readonly { sectionKey: string; sortOrder: number }[],
): string[] {
  const firstSeen = new Map<string, number>();
  for (const q of questions) {
    const prev = firstSeen.get(q.sectionKey);
    if (prev === undefined || q.sortOrder < prev) firstSeen.set(q.sectionKey, q.sortOrder);
  }
  return [...firstSeen.entries()].sort((a, b) => a[1] - b[1]).map(([key]) => key);
}

/** Display label for a section, taken from any question that belongs to it. */
export function sectionLabelFrom(
  questions: readonly { sectionKey: string; sectionLabel: string }[],
  sectionKey: string,
): string {
  return questions.find((q) => q.sectionKey === sectionKey)?.sectionLabel ?? sectionKey;
}

// ────────────────────────────────────────────────────────────────────────────
// Open questions still unresolved:
//
//  1. UNIT TAXONOMY — UnitType has no counterpart in My.Directory, whose
//     ResourceType is Driver | Truck | Rigid | Trailer | Other. Lgv, Bus,
//     Minibus, Car, Motorcycle and Cycle cannot be represented as a resource,
//     so a check could point at a resource whose type contradicts its
//     template's unit. Blocks template-to-vehicle assignment.
//  2. IN-FLIGHT CHECKS — what happens to a check open on a driver's phone
//     when the template is republished?
//  3. MULTIPLE TEMPLATES PER UNIT — nothing prevents two HGV templates, and
//     nothing resolves which one a driver receives.
//  4. WALKAROUND WRAPPER — odometer, vehicle selection and the driver
//     declaration exist in the schema (CheckSessions) but no UI touches them.
//  5. LGV SCOPE — LGV/Van sits under the WAC regime. A 3.5t van is generally
//     outside formal walkaround scope; the split may want to follow weight /
//     O-licence status rather than body shape.
//  6. COUNTRY SCOPING — the BE catalogue carries `countries` on every entry,
//     all shipping as "*". RSA (IE) and DVSA (GB/XI) lists differ; nothing
//     narrows them yet.
// ────────────────────────────────────────────────────────────────────────────
